import { createContext, ReactNode, useContext, useState } from 'react';

/**
 * Whether the knowledge sidebar is collapsed to its icon rail, and a way to
 * change it. The host app supplies it, since it alone can remember the choice
 * for the user.
 */
export type UseSidebarCollapsed = () => readonly [
  isCollapsed: boolean,
  setCollapsed: (isCollapsed: boolean) => void,
];

/** Without a host, the choice holds until the page is left. */
const useLocalSidebarCollapsed: UseSidebarCollapsed = () => useState(false);

const SidebarCollapsedContext = createContext<UseSidebarCollapsed>(
  useLocalSidebarCollapsed,
);

/**
 * Injected via the `useSidebarCollapsed` prop on the knowledge management
 * entry. The value is called as a hook, so it must stay the same for the
 * lifetime of the provider.
 */
export function SidebarCollapsedProvider({
  value,
  children,
}: {
  value: UseSidebarCollapsed | undefined;
  children: ReactNode;
}): JSX.Element {
  return (
    <SidebarCollapsedContext.Provider value={value ?? useLocalSidebarCollapsed}>
      {children}
    </SidebarCollapsedContext.Provider>
  );
}

export function useSidebarCollapsed(): ReturnType<UseSidebarCollapsed> {
  const useHostSidebarCollapsed = useContext(SidebarCollapsedContext);
  return useHostSidebarCollapsed();
}
